import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { arrayOf, object, bool, shape, number } from 'prop-types';
import { useIntl, FormattedMessage } from '../../util/reactIntl';

import { LayoutComposer, NamedLink, PaginationLinks } from '../../components';
import StaticPage from '../PageBuilder/StaticPage';
import pageBuilderCss from '../PageBuilder/PageBuilder.module.css';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './BlogPage.module.css';

const layoutAreas = `
  topbar
  main
  footer
`;

const formatDate = (iso, intl) => {
  if (!iso) {
    return '';
  }
  try {
    return intl.formatDate(new Date(iso), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (e) {
    return '';
  }
};

/**
 * Public blog index — published posts from Firebase (SSR via loadData).
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
export const BlogPageComponent = props => {
  const { posts, pagination, fetchInProgress, fetchError } = props;
  const intl = useIntl();

  const schemaTitle = intl.formatMessage({ id: 'BlogPage.schemaTitle' });
  const schemaDescription = intl.formatMessage({ id: 'BlogPage.schemaDescription' });

  return (
    <StaticPage
      title={schemaTitle}
      description={schemaDescription}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Blog',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutComposer areas={layoutAreas} className={pageBuilderCss.layout}>
        {composerProps => {
          const { Topbar, Main, Footer } = composerProps;
          return (
            <>
              <Topbar as="header" className={pageBuilderCss.topbar}>
                <TopbarContainer currentPage="BlogPage" />
              </Topbar>

              <Main
                as="main"
                id="main-content"
                className={classNames(pageBuilderCss.main, css.pageMain)}
              >
                <header className={css.hero}>
                  <div className={css.heroInner}>
                    <p className={css.kicker}>
                      <FormattedMessage id="BlogPage.kicker" />
                    </p>
                    <h1 className={css.heading}>
                      <FormattedMessage id="BlogPage.heading" />
                    </h1>
                    <p className={css.intro}>
                      <FormattedMessage id="BlogPage.intro" />
                    </p>
                  </div>
                </header>

                <section className={css.listSection} aria-live="polite">
                  {fetchInProgress && (!posts || posts.length === 0) ? (
                    <p className={css.loading}>
                      <FormattedMessage id="BlogPage.loading" />
                    </p>
                  ) : null}

                  {fetchError ? (
                    <p className={css.error}>
                      <FormattedMessage id="BlogPage.error" />
                    </p>
                  ) : null}

                  {!fetchInProgress && !fetchError && (!posts || posts.length === 0) ? (
                    <p className={css.empty}>
                      <FormattedMessage id="BlogPage.empty" />
                    </p>
                  ) : null}

                  {posts && posts.length > 0 ? (
                    <>
                      <ul className={css.postList}>
                        {posts.map(post => (
                          <li key={post.id} className={css.postCard}>
                            {post.coverImageUrl ? (
                              <NamedLink
                                name="BlogPostPage"
                                params={{ slug: post.slug }}
                                className={css.coverLink}
                              >
                                <img src={post.coverImageUrl} alt="" className={css.cover} />
                              </NamedLink>
                            ) : (
                              <div className={css.coverLink} aria-hidden="true" />
                            )}
                            <div>
                              <p className={css.postMeta}>
                                {[formatDate(post.publishedAt, intl), post.author]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                              <NamedLink
                                name="BlogPostPage"
                                params={{ slug: post.slug }}
                                className={css.titleLink}
                              >
                                <h2 className={css.postTitle}>{post.title}</h2>
                              </NamedLink>
                              {post.excerpt ? <p className={css.excerpt}>{post.excerpt}</p> : null}
                              {post.tags && post.tags.length > 0 ? (
                                <ul className={css.tags}>
                                  {post.tags.map(tag => (
                                    <li key={tag} className={css.tag}>
                                      {tag}
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                              <NamedLink
                                name="BlogPostPage"
                                params={{ slug: post.slug }}
                                className={css.readMore}
                              >
                                <FormattedMessage id="BlogPage.readMore" />
                              </NamedLink>
                            </div>
                          </li>
                        ))}
                      </ul>

                      {pagination && pagination.totalPages > 1 ? (
                        <div className={css.pagination}>
                          <PaginationLinks
                            pageName="BlogPage"
                            pagePathParams={{}}
                            pageSearchParams={{}}
                            pagination={pagination}
                          />
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

BlogPageComponent.propTypes = {
  posts: arrayOf(object),
  pagination: shape({
    page: number,
    perPage: number,
    totalItems: number,
    totalPages: number,
  }),
  fetchInProgress: bool,
  fetchError: object,
};

const mapStateToProps = state => {
  const { posts, pagination, fetchInProgress, fetchError } = state.BlogPage || {};
  return {
    posts,
    pagination,
    fetchInProgress,
    fetchError,
  };
};

const BlogPage = compose(connect(mapStateToProps))(BlogPageComponent);

export default BlogPage;
